/**
 * Task: ArticleList — category-grouped, clickable list of articles.
 */
import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { FileText } from 'lucide-react';

export default function ArticleList({ articles, selectedSlug, onSelect, isSearchResults }) {
  const grouped = useMemo(() => {
    const out = {};
    (articles || []).forEach((a) => {
      const cat = a.category || 'Other';
      if (!out[cat]) out[cat] = [];
      out[cat].push(a);
    });
    return out;
  }, [articles]);

  const categoryOrder = [
    'Onboarding',
    'Bookings',
    'Job Cards',
    'Quotes & Invoices',
    'Customers',
    'Mechanic Operations',
    'Financials',
    'Comms & Marketing',
    'Templates',
    'Configuration',
    'Compliance',
    'Other',
  ];

  const sortedCategories = useMemo(() => {
    return Object.keys(grouped).sort((a, b) => {
      const ai = categoryOrder.indexOf(a);
      const bi = categoryOrder.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
     
  }, [grouped]);

  if (articles.length === 0) {
    return (
      <Card className="p-6 text-center text-sm text-slate-500" data-testid="article-list-empty">
        No articles match your search.
      </Card>
    );
  }

  return (
    <Card className="p-0 overflow-hidden max-h-[78vh] overflow-y-auto" data-testid="article-list">
      {isSearchResults && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-amber-800">
          Search results
        </div>
      )}
      {sortedCategories.map((cat) => (
        <div key={cat}>
          {!isSearchResults && (
            <div className="bg-slate-100 px-4 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-600 border-b border-slate-200">
              {cat}
            </div>
          )}
          {grouped[cat].map((a) => {
            const active = a.slug === selectedSlug;
            return (
              <button
                key={a.slug}
                type="button"
                onClick={() => onSelect(a.slug)}
                data-testid={`article-list-item-${a.slug}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors hover:bg-blue-50 ${
                  active
                    ? 'bg-blue-100 border-l-4 border-l-blue-600 pl-3'
                    : 'border-l-4 border-l-transparent'
                }`}
              >
                <div className="flex items-start gap-2">
                  <FileText className={`h-3.5 w-3.5 mt-1 flex-shrink-0 ${active ? 'text-blue-700' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-snug ${active ? 'font-bold text-blue-900' : 'font-medium text-slate-800'}`}>
                      {a.title}
                    </p>
                    {a.summary && (
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{a.summary}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </Card>
  );
}
